import React, { useCallback, useEffect, useRef, useState } from 'react';
import APIService from '../../services/api';
import { isAoAdmin } from '../AoSociete';
import CriteresEditeur, { CRITERES_DEPART, nettoyerCriteres as nettoyer } from './CriteresEditeur';
import { lire, ecrire, deposer, copier, dateCourte } from './annoncesApi';

/**
 * L'ANNONCE d'un besoin ou d'un vivier — le tiroir qui la crée, la règle et
 * reçoit ses candidatures.
 *
 * Demande du 12.10 : « quand je crée un job, il me donne le choix : un vivier
 * (créé avec la JD) ou un besoin existant dont on reprend la JD » ; « les
 * critères éliminatoires, genre européen » ; « les gens postulent
 * directement sur LinkedIn ». D'où trois onglets :
 *  - Candidatures : retenues / refusées (avec la raison, et « Repêcher »),
 *    et le dépôt des CV reçus sur LinkedIn (traitement de fond) ;
 *  - Offre LinkedIn : ce qu'il faut coller dans LinkedIn, selon que les
 *    candidats postulent sur ERPIA (site externe) ou sur LinkedIn
 *    (candidature simplifiée : les mêmes critères en questions LinkedIn) ;
 *  - Texte et critères.
 *
 * Ouvert depuis un besoin : la cible est ce besoin. Depuis le Vivier : la
 * liste des annonces de viviers, et « Nouvelle annonce » qui donne le choix
 * vivier (existant ou nouveau) ou besoin.
 *
 * Routes : /annonces (GET, POST), /annonces/criteres, /annonces/viviers,
 * /annonces/:id (GET, PATCH), /annonces/:id/candidatures, …/repecher,
 * /annonces/:id/offre-linkedin (GET, PUT), /annonces/:id/cv, /jobs/:id.
 */
const BI = ({ icon, className }) => <i className={`bi bi-${icon}${className ? ` ${className}` : ''}`} aria-hidden="true" />;

function LienCopiable({ url, libelle = 'Copier le lien', onCopie }) {
  return (
    <div className="ann-lien">
      <a href={url} target="_blank" rel="noreferrer" className="ann-lien__url">{url}</a>
      <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={async () => onCopie(await copier(url))}><BI icon="clipboard" /> {libelle}</button>
    </div>
  );
}

// ── Création ────────────────────────────────────────────────────────────────
function Creation({ cible: cibleFixe, besoin, registre, onCreee, showNotification }) {
  const [cible, setCible] = useState(cibleFixe || 'vivier');
  const [viviers, setViviers] = useState(null);
  const [vivier, setVivier] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [besoins, setBesoins] = useState(null);
  const [besoinId, setBesoinId] = useState(besoin?.id ? String(besoin.id) : '');
  const [titre, setTitre] = useState('');
  const [lieu, setLieu] = useState('');
  const [texte, setTexte] = useState('');
  const [criteres, setCriteres] = useState(CRITERES_DEPART);
  const [cvObligatoire, setCvObligatoire] = useState(true);
  const [anonyme, setAnonyme] = useState(true);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (cibleFixe === 'besoin') return;
    lire('/annonces/viviers').then(j => { if (!j.error && !j.absent) { setViviers(j.viviers || []); setVivier((j.viviers || [])[0]?.id || '__nouveau'); } });
    APIService.getBesoins().then(r => setBesoins((r.besoins || []).filter(b => !b.archived_at && (b.type_carte || 'besoin') === 'besoin'))).catch(() => setBesoins([]));
  }, [cibleFixe]);

  const creer = async () => {
    setOccupe(true); setErreur(null);
    const corps = { cible, criteres: nettoyer(criteres), cv_obligatoire: cvObligatoire };
    if (cible === 'besoin') Object.assign(corps, { besoin_id: Number(besoinId), anonyme });
    else Object.assign(corps, vivier === '__nouveau' ? { vivier_nouveau: nouveau } : { vivier }, { titre, lieu, texte });
    const j = await ecrire('POST', '/annonces', corps);
    setOccupe(false);
    if (j.status === 409 && j.existante?.id) { showNotification?.(j.error, 'error'); onCreee(j.existante.id); return; }
    if (j.error) { setErreur(j.error); return; }
    showNotification?.(j.message || 'Annonce ouverte.', 'success');
    onCreee(j.annonce.id, j.vivier_cree);
  };

  const pret = cible === 'besoin' ? !!besoinId : ((vivier && vivier !== '__nouveau') || nouveau.trim().length >= 2) && texte.trim().length >= 80;
  return (
    <div className="ann-bloc">
      {!cibleFixe && (
        <div className="ann-choix" role="radiogroup" aria-label="À quoi sert l'annonce">
          <button type="button" role="radio" aria-checked={cible === 'vivier'} className={`ann-choix__btn${cible === 'vivier' ? ' is-on' : ''}`} onClick={() => setCible('vivier')}>
            <strong>Pour un vivier</strong><span>Des profils à garder sous la main : ceux qui passent les critères entrent dans le vivier.</span>
          </button>
          <button type="button" role="radio" aria-checked={cible === 'besoin'} className={`ann-choix__btn${cible === 'besoin' ? ' is-on' : ''}`} onClick={() => setCible('besoin')}>
            <strong>Pour un besoin</strong><span>Un poste ouvert : on reprend sa fiche de poste, les candidats entrent dans le besoin.</span>
          </button>
        </div>
      )}

      {cible === 'besoin' && !cibleFixe && (
        <label className="ann-champ"><span>Besoin</span>
          <select className="erp-field" value={besoinId} onChange={e => setBesoinId(e.target.value)}>
            <option value="">{besoins == null ? 'Chargement…' : 'Choisissez le besoin…'}</option>
            {(besoins || []).map(b => <option key={b.id} value={b.id}>{b.titre}{b.client ? ` — ${b.client}` : ''}</option>)}
          </select>
        </label>
      )}
      {cible === 'besoin' && (
        <>
          <p className="ann-texte">Le texte reprend la fiche de poste du besoin ; vous pourrez le modifier une fois l'annonce ouverte.</p>
          <label className="ann-case"><input type="checkbox" checked={anonyme} onChange={e => setAnonyme(e.target.checked)} /> Masquer le nom du client</label>
        </>
      )}
      {cible === 'vivier' && (
        <>
          <label className="ann-champ"><span>Vivier</span>
            <select className="erp-field" value={vivier} onChange={e => setVivier(e.target.value)}>
              {viviers == null && <option value="">Chargement…</option>}
              {(viviers || []).map(v => <option key={v.id} value={v.id}>{v.libelle}</option>)}
              <option value="__nouveau">+ Nouveau vivier…</option>
            </select>
          </label>
          {vivier === '__nouveau' && (
            <label className="ann-champ"><span>Nom du nouveau vivier</span>
              <input className="erp-field" value={nouveau} onChange={e => setNouveau(e.target.value)} placeholder="Data engineers" />
            </label>
          )}
          <label className="ann-champ"><span>Titre du job LinkedIn <em>publié — le nom du vivier sinon</em></span>
            <input className="erp-field" value={titre} onChange={e => setTitre(e.target.value)} placeholder="Data engineer — missions en Suisse romande" />
          </label>
          <label className="ann-champ"><span>Lieu <em>exigé pour LinkedIn</em></span>
            <input className="erp-field" value={lieu} onChange={e => setLieu(e.target.value)} placeholder="Genève, Lausanne" />
          </label>
          <label className="ann-champ"><span>Texte de l'annonce</span>
            <textarea className="erp-field ann-zone" rows={8} value={texte} onChange={e => setTexte(e.target.value)} placeholder="Le profil que vous recherchez, les missions type, ce que vous proposez…" />
            <span className={`ann-compteur${texte.trim().length < 80 ? ' is-court' : ''}`}>{texte.trim().length < 80 ? `encore ${80 - texte.trim().length} caractères au moins` : `${texte.trim().length} caractères`}</span>
          </label>
        </>
      )}

      <h3 className="ann-sous-titre">Critères éliminatoires</h3>
      <CriteresEditeur valeur={criteres} onChange={setCriteres} registre={registre} lieuDefaut={besoin?.localisation || lieu} />
      <label className="ann-case"><input type="checkbox" checked={cvObligatoire} onChange={e => setCvObligatoire(e.target.checked)} /> CV obligatoire pour postuler</label>

      {erreur && <p className="ann-erreur" role="alert">{erreur}</p>}
      <button type="button" className="erp-btn erp-btn--primary ann-action" onClick={creer} disabled={occupe || !pret}>
        <BI icon={occupe ? 'arrow-repeat' : 'megaphone'} className={occupe ? 'ann-tourne' : ''} /> Ouvrir l'annonce
      </button>
    </div>
  );
}

// ── Candidatures ────────────────────────────────────────────────────────────
function Candidatures({ annonce, showNotification, onCompte }) {
  const [liste, setListe] = useState(null);
  const [filtre, setFiltre] = useState('tout');
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(null);
  const [depot, setDepot] = useState(null); // { job, resultat }
  const entree = useRef(null);
  const sonde = useRef(null);

  const charger = useCallback(async () => {
    const j = await lire(`/annonces/${annonce.id}/candidatures`);
    if (j.error) setErreur(j.error); else if (!j.absent) setListe(j.candidatures || []);
  }, [annonce.id]);
  useEffect(() => { charger(); return () => clearTimeout(sonde.current); }, [charger]);

  const suivre = useCallback((jobId) => {
    clearTimeout(sonde.current);
    sonde.current = setTimeout(async () => {
      const j = await lire(`/jobs/${jobId}`);
      const job = j.data;
      if (!job) { setDepot(null); return; }
      setDepot(d => ({ ...d, job }));
      if (['done', 'error', 'cancelled'].includes(job.statut)) { charger(); onCompte?.(); return; }
      suivre(jobId);
    }, 2000);
  }, [charger, onCompte]);

  const deposerCv = async (fichiers) => {
    const liste = [...(fichiers || [])];
    if (!liste.length) return;
    setErreur(null);
    const j = await deposer(`/annonces/${annonce.id}/cv`, liste);
    if (entree.current) entree.current.value = '';
    if (j.error) { setErreur(j.error); return; }
    showNotification?.(j.message || 'CV en cours de lecture.', 'success');
    setDepot({ job: j.job });
    suivre(j.job.id);
  };

  const repecher = async (c) => {
    setOccupe(c.id); setErreur(null);
    const j = await ecrire('POST', `/annonces/${annonce.id}/candidatures/${c.id}/repecher`, {});
    setOccupe(null);
    if (j.error) { setErreur(j.error); return; }
    showNotification?.(j.message, 'success');
    charger(); onCompte?.();
  };

  const retenues = (liste || []).filter(c => c.verdict === 'retenu');
  const refusees = (liste || []).filter(c => c.verdict === 'refuse');
  const vues = filtre === 'retenu' ? retenues : filtre === 'refuse' ? refusees : (liste || []);
  const ou = annonce.cible === 'besoin' ? 'le besoin' : 'le vivier';
  const job = depot?.job;
  const fini = job && ['done', 'error', 'cancelled'].includes(job.statut);

  return (
    <div className="ann-bloc">
      <div className="ann-depot">
        <div>
          <strong>Candidatures reçues sur LinkedIn</strong>
          <p className="ann-texte">Ils arrivent seuls par la boîte candidatures : LinkedIn y envoie un mail par candidat, CV joint, et chacun entre dans {ou}. Un CV reçu autrement se dépose ici, sans doublon.</p>
        </div>
        <label className="erp-btn erp-btn--secondary ann-depot__btn">
          <BI icon="upload" /> Déposer des CV
          <input ref={entree} type="file" multiple accept=".pdf,.doc,.docx,.odt,.rtf" className="ann-sr" onChange={e => deposerCv(e.target.files)} />
        </label>
      </div>
      {job && (
        <div className="ann-job" aria-live="polite">
          {!fini && <p className="ann-texte"><BI icon="arrow-repeat" className="ann-tourne" /> {job.label || 'Lecture des CV'} — {job.done}/{job.total}</p>}
          {fini && job.statut === 'error' && <p className="ann-erreur">{job.error}</p>}
          {fini && job.result && (
            <>
              <p className="ann-ok">{job.result.entres} candidat{job.result.entres > 1 ? 's' : ''} entré{job.result.entres > 1 ? 's' : ''} dans {ou}{job.result.erreurs ? ` · ${job.result.erreurs} CV non lu${job.result.erreurs > 1 ? 's' : ''}` : ''}.</p>
              {(job.result.resultats || []).filter(r => r.erreur).map(r => <p key={r.fichier} className="ann-muet"><span className="ann-mono">{r.fichier}</span> : {r.erreur}</p>)}
            </>
          )}
        </div>
      )}

      {erreur && <p className="ann-erreur" role="alert">{erreur}</p>}
      <div className="ann-filtres" role="tablist" aria-label="Filtrer les candidatures">
        {[['tout', 'Toutes', liste?.length || 0], ['retenu', 'Retenues', retenues.length], ['refuse', 'Refusées', refusees.length]].map(([k, l, n]) => (
          <button key={k} type="button" role="tab" aria-selected={filtre === k} className={`ann-filtre${filtre === k ? ' is-on' : ''}`} onClick={() => setFiltre(k)}>{l} <span className="ann-mono">{n}</span></button>
        ))}
      </div>
      {liste == null && !erreur && <p className="ann-muet"><BI icon="arrow-repeat" className="ann-tourne" /> Chargement…</p>}
      {liste && vues.length === 0 && (
        <p className="ann-muet">{liste.length === 0 ? 'Aucune candidature pour l\'instant. Partagez le lien « Postuler », ou déposez les CV reçus sur LinkedIn.' : 'Rien dans ce filtre.'}</p>
      )}
      <ul className="ann-liste">
        {vues.map(c => (
          <li key={c.id} className={`ann-cand${c.verdict === 'refuse' ? ' is-refuse' : ''}`}>
            <div className="ann-cand__ligne">
              <span className={`ann-pastille ann-pastille--${c.verdict === 'refuse' ? (c.repeche ? 'repeche' : 'refuse') : 'retenu'}`}>{c.verdict === 'refuse' ? (c.repeche ? 'Repêché·e' : 'Refusée') : 'Retenue'}</span>
              {c.candidate_id ? <a className="ann-cand__nom" href={`#/candidats/${c.candidate_id}`}>{`${c.prenom || ''} ${c.nom || ''}`.trim() || '—'}</a> : <span className="ann-cand__nom">{`${c.prenom || ''} ${c.nom || ''}`.trim() || '—'}</span>}
              <span className="ann-cand__meta">{c.source_libelle} · <span className="ann-mono">{dateCourte(c.created_at)}</span></span>
            </div>
            {c.verdict === 'refuse' && (c.motifs || []).map(m => <p key={m.critere} className="ann-cand__motif"><BI icon="x-circle" /> {m.raison}</p>)}
            {c.repeche && <p className="ann-muet">Repêché·e{c.repeche.par ? ` par ${c.repeche.par}` : ''} le {dateCourte(c.repeche.le)}.</p>}
            {(c.reponses || []).length > 0 && (
              <details className="ann-cand__rep"><summary>Réponses</summary>
                <ul>{c.reponses.map((r, i) => <li key={i}><span>{r.question}</span> <strong>{r.reponse}</strong></li>)}</ul>
              </details>
            )}
            {c.verdict === 'refuse' && !c.repeche && c.candidate_id && (
              <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => repecher(c)} disabled={occupe === c.id}>
                <BI icon={occupe === c.id ? 'arrow-repeat' : 'arrow-return-right'} className={occupe === c.id ? 'ann-tourne' : ''} /> Faire entrer dans {ou}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── L'offre confiée à l'agent ───────────────────────────────────────────────
// L'agent du moteur de sourcing publie l'offre dans LinkedIn comme une
// personne (à blanc sur demande). Ses candidats arrivent par la boîte
// candidatures — LinkedIn y envoie un mail par candidat, CV joint. C'est la
// seule porte (décision du 12.10) : l'agent publie, il ne relève pas.
// L'état vient du serveur (`annonce.agent`) ; l'écran ne le devine pas.
const ETATS_AGENT = {
  demandee: { libelle: 'En file', ton: 'repeche' },
  brouillon: { libelle: 'Remplie à blanc', ton: 'repeche' },
  publiee: { libelle: 'Publiée', ton: 'retenu' },
  bloquee: { libelle: 'Bloquée', ton: 'refuse' },
  erreur: { libelle: 'Pas aboutie', ton: 'refuse' },
};
function ParLAgent({ annonce, mode, showNotification, onMaj }) {
  const ag = annonce.agent;
  const [aBlanc, setABlanc] = useState(false); // publication directe (offres illimitées) ; à blanc sur demande
  const [occupe, setOccupe] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [absent, setAbsent] = useState(false);
  const relire = async () => { const j = await lire(`/annonces/${annonce.id}`); if (!j.error && j.annonce) onMaj?.(j.annonce); };
  const appeler = async (quoi, chemin, corps) => {
    setOccupe(quoi); setErreur(null);
    const j = await ecrire('POST', chemin, corps);
    setOccupe(null);
    if (j.absent) { setAbsent(true); return; }
    if (j.error) { setErreur(j.error); return; }
    showNotification?.(j.message, 'success');
    relire();
  };
  const etat = ag ? ETATS_AGENT[ag.statut] : null;
  return (
    <section className="ann-agent" aria-live="polite">
      <div className="ann-agent__tete">
        <strong>Par l'agent</strong>
        {etat && <span className={`ann-pastille ann-pastille--${etat.ton}`}>{etat.libelle}</span>}
      </div>
      {absent ? <p className="ann-muet">L'offre confiée à l'agent arrive côté serveur : rien n'est encore branché.</p> : (
        <>
          <p className="ann-texte">L'agent remplit l'offre dans LinkedIn comme vous le feriez, avec les questions de cette annonce. Il ne paie jamais : si LinkedIn demande un budget, il s'arrête et vous le dit.</p>
          {ag && ag.raison && <p className={ag.statut === 'bloquee' || ag.statut === 'erreur' ? 'ann-cand__motif' : 'ann-muet'}>{ag.raison}</p>}
          {ag && ag.demande_le && <p className="ann-muet">Demandée le <span className="ann-mono">{dateCourte(ag.demande_le)}</span>{ag.publier ? ', pour publication' : ', à blanc'}.</p>}
          {/* Faire publier par l'agent : administrateur seulement (27.10). */}
          {ag?.statut !== 'publiee' && isAoAdmin(APIService.getCurrentUser()) && (
            <div className="ann-ligne">
              <label className="ann-case"><input type="checkbox" checked={aBlanc} onChange={e => setABlanc(e.target.checked)} /> Passage à blanc : remplir sans publier</label>
              <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" disabled={!!occupe || ag?.statut === 'demandee'}
                onClick={() => appeler('publier', `/annonces/${annonce.id}/offre-linkedin/agent`, { publier: !aBlanc, candidature: mode })}>
                <BI icon={occupe === 'publier' ? 'arrow-repeat' : 'send'} className={occupe === 'publier' ? 'ann-tourne' : ''} /> {aBlanc ? "Faire remplir par l'agent" : "Faire publier par l'agent"}
              </button>
            </div>
          )}
          {erreur && <p className="ann-erreur" role="alert">{erreur}</p>}
        </>
      )}
    </section>
  );
}

// ── Offre LinkedIn ──────────────────────────────────────────────────────────
function OffreLinkedIn({ annonce, showNotification, onMaj }) {
  const [o, setO] = useState(null);
  const [mode, setMode] = useState('erpia');
  const [url, setUrl] = useState('');
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const dire = (ok) => showNotification?.(ok ? 'Copié.' : 'La copie a échoué : sélectionnez le texte à la main.', ok ? 'success' : 'error');

  useEffect(() => {
    lire(`/annonces/${annonce.id}/offre-linkedin`).then(j => {
      if (j.error) { setErreur(j.error); return; }
      if (j.absent) return;
      setO(j); if (j.offre?.mode) setMode(j.offre.mode);
    });
  }, [annonce.id]);

  const enregistrer = async (retirer = false) => {
    setOccupe(true); setErreur(null);
    const j = await ecrire('PUT', `/annonces/${annonce.id}/offre-linkedin`, retirer ? { url: null } : { url, mode });
    setOccupe(false);
    if (j.error) { setErreur(j.error); return; }
    showNotification?.(j.message, 'success');
    setO(x => ({ ...x, offre: j.annonce.offre_linkedin }));
    setUrl('');
    onMaj?.(j.annonce);
  };

  if (!o) return erreur ? <p className="ann-erreur">{erreur}</p> : <p className="ann-muet"><BI icon="arrow-repeat" className="ann-tourne" /> Chargement…</p>;
  return (
    <div className="ann-bloc">
      <p className="ann-texte">ERPIA prépare l'offre ; elle se publie dans LinkedIn, à votre nom ou à celui de la page entreprise. Deux façons d'y recevoir les candidats :</p>
      <div className="ann-choix" role="radiogroup" aria-label="Où les candidats postulent">
        <button type="button" role="radio" aria-checked={mode === 'erpia'} className={`ann-choix__btn${mode === 'erpia' ? ' is-on' : ''}`} onClick={() => setMode('erpia')}>
          <strong>Ils postulent sur ERPIA</strong><span>« Site externe » dans LinkedIn : chaque candidature arrive ici, jugée par vos critères.</span>
        </button>
        <button type="button" role="radio" aria-checked={mode === 'linkedin'} className={`ann-choix__btn${mode === 'linkedin' ? ' is-on' : ''}`} onClick={() => setMode('linkedin')}>
          <strong>Ils postulent sur LinkedIn</strong><span>Candidature simplifiée : plus de candidats ; vous déposez ensuite ici les CV retenus.</span>
        </button>
      </div>

      <ParLAgent annonce={annonce} mode={mode} showNotification={showNotification} onMaj={onMaj} />
      <h3 className="ann-sous-titre">Ou à la main</h3>
      <ol className="ann-etapes">
        <li>
          <span>Ouvrez la création d'offre dans LinkedIn.</span>
          <a className="erp-btn erp-btn--secondary erp-btn--sm" href={o.url_creation} target="_blank" rel="noreferrer"><BI icon="box-arrow-up-right" /> Ouvrir LinkedIn</a>
        </li>
        <li>
          <span>Collez chaque champ :</span>
          <div className="ann-champs-copie">
            {(o.champs || []).map(c => (
              <div key={c.cle} className="ann-copie">
                <span className="ann-copie__nom">{c.libelle}</span>
                <span className={`ann-copie__val${c.cle === 'description' ? ' is-long' : ''}`}>{c.valeur}</span>
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={async () => dire(await copier(c.valeur))}><BI icon="clipboard" /> Copier</button>
              </div>
            ))}
          </div>
        </li>
        {mode === 'erpia' ? (
          <li>
            <span>Candidature : choisissez « site web externe » et collez ce lien.</span>
            <LienCopiable url={o.url_candidature} onCopie={dire} />
          </li>
        ) : (
          <li>
            <span>Ajoutez ces questions de présélection, et cochez « qualification indispensable » pour celles qui écartent :</span>
            {(o.questions_linkedin || []).length === 0
              ? <p className="ann-muet">Aucun critère sur cette annonce.</p>
              : <ul className="ann-qli">{o.questions_linkedin.map(q => (
                <li key={q.id}><strong>{q.type}</strong> — {q.reglage}{q.indispensable && <span className="ann-pastille ann-pastille--refuse">indispensable</span>}</li>
              ))}</ul>}
          </li>
        )}
        <li>
          <span>Une fois publiée, collez l'adresse de l'offre : elle reste attachée à l'annonce{mode === 'linkedin' ? ', et les CV se déposent dans l\'onglet Candidatures' : ''}.</span>
          {o.offre?.url ? (
            <div className="ann-lien">
              <a href={o.offre.url} target="_blank" rel="noreferrer" className="ann-lien__url">{o.offre.url}</a>
              <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => enregistrer(true)} disabled={occupe}><BI icon="x-circle" /> Ne plus suivre</button>
            </div>
          ) : (
            <div className="ann-lien">
              <input className="erp-field ann-lien__saisie" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.linkedin.com/jobs/view/…" aria-label="Adresse de l'offre LinkedIn publiée" />
              <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={() => enregistrer(false)} disabled={occupe || !url.trim()}>Enregistrer</button>
            </div>
          )}
        </li>
      </ol>
      {erreur && <p className="ann-erreur" role="alert">{erreur}</p>}
    </div>
  );
}

// ── Le texte rédigé par Leman (annonce de vivier) ───────────────────────────
// Tant qu'une personne ne l'a pas validé, la page « Postuler » n'existe pas et
// l'offre LinkedIn demandée attend : on le DIT, avec ce qu'il faut relire.
// Valider est un geste d'écran (le copilote ne valide pas).
function TexteLeman({ annonce, showNotification, onMaj, onRelire }) {
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);
  const t = annonce.texte_leman;
  if (!t) return null;
  if (!annonce.a_valider) {
    return (
      <p className="ann-leman-ok"><BI icon="check2-circle" /> Texte rédigé par Leman, validé{t.validation?.par ? ` par ${t.validation.par}` : ''}{t.validation?.le ? ` le ${dateCourte(t.validation.le)}` : ''}.</p>
    );
  }
  const aRelire = (t.controles?.points || []).filter(p => !p.ok && p.cle !== 'a_verifier');
  const valider = async () => {
    setOccupe(true); setErreur(null);
    const j = await ecrire('POST', `/annonces/${annonce.id}/valider`, {});
    setOccupe(false);
    if (j.absent) { setErreur('La validation arrive côté serveur : rien n\'a été changé.'); return; }
    if (j.error) { setErreur(j.error); return; }
    showNotification?.(j.message, j.apres_validation?.linkedin && !j.apres_validation.linkedin.ok ? 'error' : 'success');
    if (j.annonce) onMaj?.(j.annonce);
  };
  return (
    <section className="ann-leman" aria-live="polite">
      <div className="ann-leman__tete">
        <BI icon="file-earmark-text" className="ann-leman__icone" />
        <div>
          <strong>Texte rédigé par Leman, sans client ni secteur — à relire et valider</strong>
          <p>Sa page « Postuler » ne s'ouvre{(t.en_attente_validation || []).includes('linkedin') ? ", et l'offre LinkedIn demandée ne part," : ''} qu'à la validation.</p>
        </div>
      </div>
      {(t.a_verifier || []).length > 0 && (
        <div className="ann-leman__liste">
          <span>Leman ne pouvait pas savoir :</span>
          <ul>{t.a_verifier.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </div>
      )}
      {aRelire.length > 0 && (
        <ul className="ann-leman__points">{aRelire.map(p => <li key={p.cle}><BI icon="exclamation-triangle" /> {p.message}</li>)}</ul>
      )}
      {/* LinkedIn exige un lieu : sans lui, la validation est refusée (14.10). On le dit AVANT le clic. */}
      {(t.en_attente_validation || []).includes('linkedin') && !String(annonce.lieu || '').trim() && (
        <p className="ann-leman__manque"><BI icon="geo-alt" /> Indiquez le lieu de l'annonce avant de valider : l'offre LinkedIn en a besoin. <button type="button" className="ann-lien-bouton" onClick={onRelire}>Ouvrir « Texte et critères »</button></p>
      )}
      {erreur && <p className="ann-erreur" role="alert">{erreur}</p>}
      <div className="ann-ligne">
        <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={onRelire}><BI icon="pencil" /> Relire et corriger le texte</button>
        <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={valider} disabled={occupe}>
          <BI icon={occupe ? 'arrow-repeat' : 'check2-circle'} className={occupe ? 'ann-tourne' : ''} /> Valider le texte
        </button>
      </div>
    </section>
  );
}

// ── Texte et critères ───────────────────────────────────────────────────────
function Reglage({ annonce, registre, showNotification, onMaj, onModifiee }) {
  const [titre, setTitre] = useState(annonce.titre);
  // Le nom que l'équipe lit : pour un vivier, le nom de sa colonne (le
  // changer renomme le vivier) ; pour un besoin, son intitulé — rien à saisir.
  const nomVivierDepart = annonce.cible === 'vivier' ? (annonce.cible_libelle || '') : null;
  const [nomVivier, setNomVivier] = useState(nomVivierDepart || '');
  const [lieu, setLieu] = useState(annonce.lieu || '');
  const [texte, setTexte] = useState(annonce.texte);
  const [criteres, setCriteres] = useState(annonce.criteres || []);
  const [cvObligatoire, setCvObligatoire] = useState(annonce.cv_obligatoire !== false);
  const [occupe, setOccupe] = useState(null);
  const [erreur, setErreur] = useState(null);

  const envoyer = async (patch, quoi) => {
    setOccupe(quoi); setErreur(null);
    const j = await ecrire('PATCH', `/annonces/${annonce.id}`, patch);
    setOccupe(null);
    if (j.error) { setErreur(j.error); return; }
    showNotification?.(j.message, 'success');
    setCriteres(j.annonce.criteres || []);
    onMaj?.(j.annonce);
    onModifiee?.(j.annonce);
  };

  return (
    <div className="ann-bloc">
      {annonce.cible === 'vivier' && (
        <label className="ann-champ"><span>Nom du vivier <em>lu par l'équipe, jamais publié — renomme la colonne du Vivier</em></span>
          <input className="erp-field" value={nomVivier} onChange={e => setNomVivier(e.target.value)} />
        </label>
      )}
      <label className="ann-champ"><span>Titre du job LinkedIn <em>publié : page « Postuler » et LinkedIn</em></span><input className="erp-field" value={titre} onChange={e => setTitre(e.target.value)} /></label>
      <label className="ann-champ"><span>Lieu <em>exigé pour LinkedIn</em></span><input className="erp-field" value={lieu} onChange={e => setLieu(e.target.value)} /></label>
      <label className="ann-champ"><span>Texte de l'annonce</span>
        <textarea className="erp-field ann-zone" rows={12} value={texte} onChange={e => setTexte(e.target.value)} />
      </label>
      <label className="ann-case"><input type="checkbox" checked={cvObligatoire} onChange={e => setCvObligatoire(e.target.checked)} /> CV obligatoire pour postuler</label>
      <h3 className="ann-sous-titre">Critères éliminatoires</h3>
      <CriteresEditeur valeur={criteres} onChange={setCriteres} registre={registre} lieuDefaut={lieu} />
      {erreur && <p className="ann-erreur" role="alert">{erreur}</p>}
      <div className="ann-ligne">
        <button type="button" className="erp-btn erp-btn--primary" disabled={!!occupe}
          onClick={() => envoyer({ titre, ...(annonce.cible === 'vivier' && nomVivier.trim() && nomVivier.trim() !== nomVivierDepart ? { vivier_libelle: nomVivier.trim() } : {}), lieu, texte, cv_obligatoire: cvObligatoire, criteres: nettoyer(criteres) }, 'enregistrer')}>
          <BI icon={occupe === 'enregistrer' ? 'arrow-repeat' : 'check2'} className={occupe === 'enregistrer' ? 'ann-tourne' : ''} /> Enregistrer les modifications
        </button>
        <button type="button" className={`erp-btn ${annonce.ouverte ? 'erp-btn--danger' : 'erp-btn--secondary'}`} disabled={!!occupe}
          onClick={() => envoyer({ ouverte: !annonce.ouverte }, 'ouverte')}>
          <BI icon={annonce.ouverte ? 'pause-circle' : 'play-circle'} /> {annonce.ouverte ? "Fermer l'annonce" : "Rouvrir l'annonce"}
        </button>
      </div>
      <p className="ann-muet">Une annonce fermée ne prend plus de candidatures : sa page le dit au candidat.</p>
    </div>
  );
}

// ── Le tiroir ───────────────────────────────────────────────────────────────
export default function AnnonceTiroir({ cible, besoin = null, annonceId = null, onClose, showNotification, onVivierCree, onModifiee }) {
  const [vue, setVue] = useState({ etat: 'charge' }); // charge | absent | liste | creation | annonce
  const [annonce, setAnnonce] = useState(null);
  const [liste, setListe] = useState([]);
  const [registre, setRegistre] = useState(null);
  const [onglet, setOnglet] = useState('candidatures');
  const [erreur, setErreur] = useState(null);
  const ferme = useRef(null);

  const ouvrir = useCallback(async (id) => {
    const j = await lire(`/annonces/${id}`);
    if (j.error) { setErreur(j.error); return; }
    setAnnonce(j.annonce); setOnglet(j.annonce?.a_valider ? 'reglage' : 'candidatures'); setVue({ etat: 'annonce' });
  }, []);

  const recharger = useCallback(async () => {
    setErreur(null);
    const q = cible === 'besoin' ? `/annonces?besoin_id=${besoin.id}` : '/annonces';
    const [j, r] = await Promise.all([lire(q), lire('/annonces/criteres')]);
    if (j.absent || r.absent) { setVue({ etat: 'absent' }); return; }
    if (j.error) { setErreur(j.error); setVue({ etat: 'liste' }); return; }
    if (!r.error) setRegistre(r);
    const annonces = (j.annonces || []).filter(a => (cible === 'besoin' ? true : a.cible === 'vivier'));
    setListe(annonces);
    if (cible === 'besoin') {
      if (annonces.length) { setAnnonce(annonces[0]); setVue({ etat: 'annonce' }); } else setVue({ etat: 'creation' });
    } else if (annonceId && annonces.some(a => a.id === annonceId)) ouvrir(annonceId); // depuis une colonne du Vivier
    else setVue({ etat: annonces.length ? 'liste' : 'creation' });
  }, [cible, besoin, annonceId, ouvrir]);
  useEffect(() => { recharger(); }, [recharger]);

  useEffect(() => {
    if (ferme.current) ferme.current.focus();
    const echap = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [onClose]);

  const rafraichirAnnonce = useCallback(async () => {
    if (!annonce) return;
    const j = await lire(`/annonces/${annonce.id}`);
    if (!j.error && j.annonce) setAnnonce(j.annonce);
  }, [annonce]);
  const dire = (ok) => showNotification?.(ok ? 'Lien copié.' : 'La copie a échoué : sélectionnez le lien à la main.', ok ? 'success' : 'error');

  return (
    <>
      <div className="ann-fond" onClick={onClose} aria-hidden="true" />
      <aside className="ann erp-drawer" role="dialog" aria-modal="true" aria-label="Annonce">
        <header className="ann-tete">
          <BI icon="megaphone" className="ann-tete__icone" />
          <div className="ann-tete__txt">
            <h2 className="ann-titre">{vue.etat === 'annonce' && annonce ? (annonce.libelle || annonce.titre) : cible === 'besoin' ? 'Annonce du besoin' : 'Annonces des viviers'}</h2>
            {vue.etat === 'annonce' && annonce && (
              <p className="ann-tete__sous">
                <span className={`ann-pastille ann-pastille--${annonce.a_valider ? 'attente' : annonce.ouverte ? 'retenu' : 'ferme'}`}>{annonce.a_valider ? 'À valider' : annonce.ouverte ? 'Ouverte' : 'Fermée'}</span>
                {annonce.cible === 'besoin' ? 'Besoin' : 'Vivier'} · {annonce.cible_libelle}
              </p>
            )}
          </div>
          {vue.etat === 'annonce' && cible !== 'besoin' && (
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => { setAnnonce(null); recharger(); }}><BI icon="chevron-left" /> Toutes</button>
          )}
          <button ref={ferme} type="button" className="ann-x" onClick={onClose} aria-label="Fermer"><BI icon="x-lg" /></button>
        </header>

        <div className="ann-corps">
          {erreur && <p className="ann-erreur" role="alert">{erreur}</p>}
          {vue.etat === 'charge' && <p className="ann-muet"><BI icon="arrow-repeat" className="ann-tourne" /> Chargement…</p>}
          {vue.etat === 'absent' && <p className="ann-muet">Les annonces arrivent côté serveur : rien n'est encore branché.</p>}

          {vue.etat === 'liste' && (
            <div className="ann-bloc">
              <button type="button" className="erp-btn erp-btn--primary ann-action" onClick={() => setVue({ etat: 'creation' })}><BI icon="plus-lg" /> Nouvelle annonce</button>
              <ul className="ann-liste">
                {liste.map(a => (
                  <li key={a.id}>
                    <button type="button" className="ann-ligne-annonce" onClick={() => ouvrir(a.id)}>
                      <span className={`ann-pastille ann-pastille--${a.a_valider ? 'attente' : a.ouverte ? 'retenu' : 'ferme'}`}>{a.a_valider ? 'À valider' : a.ouverte ? 'Ouverte' : 'Fermée'}</span>
                      <strong>{a.libelle || a.titre}</strong>
                      <span className="ann-cand__meta">{(a.libelle || a.titre) !== a.titre ? `LinkedIn : ${a.titre} · ` : ''}{a.cible === 'besoin' ? `${a.cible_libelle} · ` : ''}{a.candidatures.retenus} retenue{a.candidatures.retenus > 1 ? 's' : ''} · {a.candidatures.refuses} refusée{a.candidatures.refuses > 1 ? 's' : ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {vue.etat === 'creation' && (
            <Creation cible={cible === 'besoin' ? 'besoin' : null} besoin={besoin} registre={registre} showNotification={showNotification}
              onCreee={(id, vivierCree) => { if (vivierCree) onVivierCree?.(); ouvrir(id); }} />
          )}

          {vue.etat === 'annonce' && annonce && (
            <>
              <TexteLeman annonce={annonce} showNotification={showNotification} onMaj={setAnnonce} onRelire={() => setOnglet('reglage')} />
              <section className="ann-postuler">
                <span className="ann-sous-titre">Lien « Postuler »</span>
                <LienCopiable url={annonce.url} onCopie={dire} />
                <span className="ann-muet">{annonce.a_valider ? 'Ce lien s\'ouvrira à la validation du texte.'
                  : `Les candidatures ${annonce.ouverte ? 'arrivent ici, jugées par vos critères' : 'ne sont plus acceptées : l\'annonce est fermée'}.`}</span>
              </section>
              <div className="ann-onglets" role="tablist" aria-label="Annonce">
                {[['candidatures', `Candidatures · ${annonce.candidatures.total}`], ['linkedin', 'Offre LinkedIn'], ['reglage', 'Texte et critères']].map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-selected={onglet === k} className={`ann-onglet${onglet === k ? ' is-on' : ''}`} onClick={() => setOnglet(k)}>{l}</button>
                ))}
              </div>
              {onglet === 'candidatures' && <Candidatures annonce={annonce} showNotification={showNotification} onCompte={rafraichirAnnonce} />}
              {onglet === 'linkedin' && <OffreLinkedIn annonce={annonce} showNotification={showNotification} onMaj={setAnnonce} />}
              {onglet === 'reglage' && <Reglage key={annonce.id} annonce={annonce} registre={registre} showNotification={showNotification} onMaj={setAnnonce} onModifiee={onModifiee} />}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
